import { useTranslation } from 'react-i18next';
import { cn } from '@/shared/lib/utils';
import { CheckIcon } from 'lucide-react';
import { WizardStep } from '../types/wizard.types';
import {
  WizardAnswers,
  InteractionFormData,
  PraiseFormData,
  FeedbackFormData,
  DebriefFormData,
} from '@momentum/types';
import { TFunction } from 'i18next';

interface ReasonDetailsConfig<TFormData extends InteractionFormData> {
  title?: string;
  steps: (keyof TFormData)[];
  afterStep: keyof TFormData;
}

interface WizardStepProgressProps<
  TFormData extends InteractionFormData = InteractionFormData
> {
  steps: WizardStep<TFormData>[];
  currentStepId: string;
  answers: WizardAnswers<TFormData>;
  reason?: ReasonDetailsConfig<TFormData>;
  className?: string;
}

interface StepItem {
  id: string;
  title: string;
  isCompleted: boolean;
  isCurrent: boolean;
  isReasonDetail?: boolean;
}

const getTitleFromReasonSelection = (
  answers: WizardAnswers<PraiseFormData | FeedbackFormData | DebriefFormData>,
  t: TFunction<['wizard']>
) => {
  if ('reason-selection' in answers) {
    const reasonSelection = answers['reason-selection'] as string[];
    return t(`stepTitles.${reasonSelection[0]}` as any);
  }
  return null;
};

export const WizardStepProgress = <
  TFormData extends InteractionFormData = InteractionFormData
>({
  steps,
  currentStepId,
  answers,
  reason,
  className = '',
}: WizardStepProgressProps<TFormData>) => {
  const { t } = useTranslation('wizard');
  // Create step items with status
  const stepItems: StepItem[] = steps.map((step) => {
    const stepId = step.id as string;

    return {
      id: stepId,
      title: t(`stepTitles.${stepId}`, {
        defaultValue: stepId.split('-').join(' '),
      }),
      isCompleted: !!answers[stepId],
      isCurrent: stepId === currentStepId,
      isReasonDetail: reason?.steps.includes(stepId) || false,
    };
  });

  // Group steps into sections based on afterStep positioning
  const commonSteps: StepItem[] = [];
  const reasonDetailSteps: StepItem[] = [];

  let afterStepIdIndex = 0;
  stepItems.forEach((step) => {
    if (step.isReasonDetail) {
      reasonDetailSteps.push(step);
    } else {
      commonSteps.push(step);
      if (step.id === reason?.afterStep) {
        afterStepIdIndex = commonSteps.length - 1;
      }
    }
  });
  commonSteps.splice(afterStepIdIndex + 1, 0, {
    id: 'reason-details',
    isCompleted:
      commonSteps.findIndex((step) => step.id === currentStepId) >
      afterStepIdIndex,
    isCurrent: !commonSteps.map((a) => a.id).includes(currentStepId),
    isReasonDetail: true,
    title:
      getTitleFromReasonSelection(
        answers as WizardAnswers<InteractionFormData>,
        t
      ) ||
      reason?.title ||
      t('stepTitles.reason-details'),
  });

  const renderStepItem = (step: StepItem, index: number, isLast = false) => (
    <div key={step.id} className="flex items-start gap-4">
      {/* Step Circle and Connector */}
      <div className="flex flex-col items-center p-0.5">
        {/* Step Circle */}
        <div
          className={cn(
            'w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold transition-all duration-200',
            step.isCurrent &&
              'bg-primary text-primary-foreground shadow-md shadow-primary/30 scale-110',
            step.isCompleted &&
              !step.isCurrent &&
              'bg-primary/90 text-primary-foreground',
            !step.isCompleted &&
              !step.isCurrent &&
              'bg-muted border border-border text-muted-foreground'
          )}
        >
          {step.isCompleted ? (
            <CheckIcon className="w-3.5 h-3.5" />
          ) : (
            <span>{index + 1}</span>
          )}
        </div>

        {/* Connecting Line */}
        {!isLast && (
          <div
            className={cn(
              'w-0.5 h-10 my-1.5 transition-all duration-200',
              step.isCompleted || step.isCurrent ? 'bg-primary/60' : 'bg-border'
            )}
          />
        )}
      </div>

      {/* Step Content */}
      <div className="flex flex-1 items-center h-8">
        <p
          className={cn(
            'text-sm font-medium transition-all duration-200 capitalize',
            step.isCurrent && 'text-foreground font-semibold',
            step.isCompleted && !step.isCurrent && 'text-foreground/70',
            !step.isCompleted && !step.isCurrent && 'text-muted-foreground'
          )}
        >
          {step.title}
        </p>
      </div>
    </div>
  );

  const completedCount = commonSteps.filter((step) => step.isCompleted).length;
  const totalCount = commonSteps.length;
  const progressPercentage = (completedCount / totalCount) * 100;

  return (
    <div
      className={cn(
        'w-full max-w-xs shadow-lg bg-white backdrop-blur-sm h-full px-8 py-12 flex flex-col',
        className
      )}
    >
      {/* Header - fixed at top */}
      <div className="mb-8 flex-shrink-0">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-wider">
            {t('common.labels.progress')}
          </h3>
          <span className="text-xs font-semibold text-primary">
            {completedCount}/{totalCount}
          </span>
        </div>
        {/* Progress bar */}
        <div className="h-1.5 bg-muted/50 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-primary to-primary/70 transition-all duration-500 ease-out rounded-full"
            style={{ width: `${progressPercentage}%` }}
          />
        </div>
      </div>

      {/* Steps list - scrollable */}
      <div className="space-y-2 flex-1 overflow-y-auto pr-2 -me-2">
        <div className="space-y-0">
          {commonSteps.map((step, index) =>
            renderStepItem(step, index, index === commonSteps.length - 1)
          )}
        </div>
      </div>
    </div>
  );
};
